<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useRouter } from 'vue-router';
import { mdiEmailOutline, mdiLockOutline, mdiEyeOutline, mdiEyeOffOutline, mdiAlertCircleOutline } from '@mdi/js';

const emit = defineEmits(['forgot-password']);

const showPassword = ref(false);
const router = useRouter();
const authStore = useAuthStore();

const otpCode = ref('');

const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required')
    .trim(),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
});

const { handleSubmit, errors, values, setFieldValue } = useForm({
  validationSchema: schema,
  initialValues: {
    email: '',
    password: ''
  }
});

// Watch for store error changes
watch(() => authStore.error, (newError) => {
  if (newError && !newError.includes('success')) {
    // Clear error after 5 seconds
    setTimeout(() => {
      authStore.clearError();
    }, 5000);
  }
});

const onSubmit = handleSubmit(async (values) => {
  try {
    const success = await authStore.login(values.email, values.password);
    if (!success) {
      console.error('Login failed');
    }
  } catch (error) {
    console.error('Login error:', error);
  }
});

const onOtpSubmit = async () => {
  await authStore.verifyOtp(otpCode.value);
};

const handleForgotPassword = () => {
  emit('forgot-password');
};
</script>

<template>
  <form @submit.prevent="authStore.pendingOtp ? onOtpSubmit() : onSubmit()" class="login-form">
    <v-text-field
      :model-value="values.email"
      @update:model-value="(val: string) => setFieldValue('email', val)"
      label="Email"
      density="comfortable"
      variant="outlined"
      hide-details="auto"
      class="input-field"
      :error-messages="errors.email"
      :disabled="authStore.loading || authStore.pendingOtp"
      autocomplete="email"
      :prepend-inner-icon="mdiEmailOutline"
    />
    
    <v-text-field
      :model-value="values.password"
      @update:model-value="(val: string) => setFieldValue('password', val)"
      label="Password"
      density="comfortable"
      variant="outlined"
      hide-details="auto"
      class="input-field"
      :append-inner-icon="showPassword ? mdiEyeOutline : mdiEyeOffOutline"
      :type="showPassword ? 'text' : 'password'"
      @click:append-inner="showPassword = !showPassword"
      :error-messages="errors.password"
      :disabled="authStore.loading || authStore.pendingOtp"
      autocomplete="current-password"
      :prepend-inner-icon="mdiLockOutline"
    />

    <v-text-field
      v-if="authStore.pendingOtp"
      v-model="otpCode"
      label="OTP Code"
      density="comfortable"
      variant="outlined"
      hide-details="auto"
      class="input-field"
      :error-messages="authStore.otpError"
      :disabled="authStore.loading"
      autocomplete="one-time-code"
      :prepend-inner-icon="mdiLockOutline"
    />

    <div class="auth-options" v-if="!authStore.pendingOtp">
      <v-checkbox
        label="Remember me"
        color="#243a70"
        density="compact"
        hide-details
        :disabled="authStore.loading"
      />
      <a 
        @click="handleForgotPassword" 
        class="forgot-link"
        style="cursor: pointer"
      >
        Forgot password?
      </a>
    </div>
    
    <v-btn 
      v-if="!authStore.pendingOtp"
      :loading="authStore.loading" 
      block 
      class="sign-in-btn" 
      type="submit"
      :disabled="authStore.loading"
    >
      Sign In
    </v-btn>
    <v-btn
      v-if="authStore.pendingOtp"
      :loading="authStore.loading"
      block
      class="sign-in-btn"
      type="submit"
      :disabled="authStore.loading || !otpCode"
    >
      Verify OTP
    </v-btn>
    
    <v-alert
      v-if="authStore.error && !authStore.pendingOtp"
      color="error"
      variant="tonal"
      density="compact"
      :icon="mdiAlertCircleOutline"
      class="error-alert"
    >
      {{ authStore.error }}
    </v-alert>
    <v-alert
      v-if="authStore.otpError && authStore.pendingOtp"
      color="error"
      variant="tonal"
      density="compact"
      :icon="mdiAlertCircleOutline"
      class="error-alert"
    >
      {{ authStore.otpError }}
    </v-alert>
  </form>
</template>

<style lang="scss">
.login-form {
  .input-field {
    margin-bottom: 20px;
    
    :deep(.v-field) {
      border-radius: 6px;
      background-color: #f8fafc;
      transition: all 0.2s ease;
      
      &.v-field--focused {
        background-color: white;
        border-color: #243a70;
        box-shadow: 0 0 0 1px #243a70;
      }
      
      &.v-field--error {
        border-color: #ef4444;
        box-shadow: 0 0 0 1px #ef4444;
      }
      
      .v-field__input {
        padding: 16px 14px;
        font-size: 15px;
      }
      
      .v-field__prepend-inner {
        padding-inline-end: 12px;
        
        i {
          color: #64748b;
        }
      }
    }
  }
  
  .auth-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;
    
    :deep(.v-selection-control) {
      min-height: auto;
      
      .v-selection-control__wrapper {
        margin-right: 6px;
      }
      
      .v-label {
        font-size: 14px;
        opacity: 0.8;
      }
    }
  }
  
  .forgot-link {
    color: #243a70;
    font-size: 14px;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
    
    &:hover {
      color: #1e2f5c;
      text-decoration: underline;
    }
  }
  
  .sign-in-btn {
    height: 48px;
    margin-bottom: 20px;
    background-color: #243a70;
    color: white;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    border-radius: 6px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 6px rgba(36, 58, 112, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
    
    &:hover, &:focus {
      background-color: #1e2f5c;
      box-shadow: 0 6px 10px rgba(36, 58, 112, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(36, 58, 112, 0.1), 0 1px 2px rgba(0, 0, 0, 0.08);
    }
    
    &.v-btn--disabled {
      background-color: rgba(36, 58, 112, 0.6) !important;
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }
  
  .error-alert {
    margin-top: 20px;
    border-radius: 6px;
    border: none;
    font-size: 14px;
    
    .v-alert__content {
      padding: 6px 0;
    }
  }
}
</style>
